import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import {
  ChatCircleDots, Sparkle, Trophy, Crosshair, TrendUp, Compass, Globe,
  NotePencil, MagnifyingGlass, CaretDown, ArrowRight, Lightbulb, ShieldCheck,
  CheckCircle, BookOpen, Lightning, ChartBar,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Self-serve Guide & Demo hub — explains the whole product, how to get the best
// results from each feature, FAQ + glossary. Everything is searchable and every
// feature deep-links into its tab via onNavigate.

const FEATURES = [
  {
    id: "advisor", title: "Advisor", tab: "copilot", icon: ChatCircleDots, accent: "violet",
    tagline: "Your grounded AI research co-pilot.",
    what: "A chat assistant that answers any startup/market question using YOUR real data — scored demand, live revenue proof, competitor checks. It cites its evidence and tells you honestly when it doesn't know.",
    problem: "Most AI tools hallucinate confident-sounding nonsense. The Advisor is grounded in 4,600+ real startups, 12,500+ demand topics and live revenue numbers, so its answers are verifiable.",
    steps: [
      "Open Advisor and type a real question — e.g. \"What's a whitespace opportunity in fitness?\"",
      "Read the answer with its inline evidence and sources.",
      "Ask follow-ups — it remembers the conversation in that session.",
    ],
    tips: [
      "Be specific: \"DTC skincare for men in Germany\" beats \"skincare\".",
      "Ask it to compare two ideas, or to pressure-test a verdict you saw elsewhere on the site.",
      "If it says \"no grounded source\", treat that as a real signal to validate first — it's not dodging, it's being honest.",
    ],
  },
  {
    id: "discover", title: "Discover", tab: "discover", icon: Sparkle, accent: "violet",
    tagline: "Proven business models worth cloning into new markets.",
    what: "216 ranked, grounded opportunities built from a VC-backed corpus (YC, a16z, Accel, Sequoia). Toggle to 💰 Bootstrapped to browse real indie earners instead.",
    problem: "Instead of guessing what to build, you start from models that are already working — then find the country/market where that model is missing.",
    steps: [
      "Pick the corpus: VC-backed (funded proof) or 💰 Bootstrapped (revenue proof).",
      "Filter by country, industry or confidence tier.",
      "Open any opportunity to see the full grounded brief, sources and the cluster of companies behind it.",
    ],
    tips: [
      "Sort by gap score — anything ≥3 is a strong signal, ≥5 is rare.",
      "Use the 💰 Bootstrapped toggle to see if a model already makes real money before you commit.",
      "Save the best ones to My Briefs so you can compare a shortlist later.",
    ],
  },
  {
    id: "evaluate", title: "Evaluate", tab: "evaluate", icon: Trophy, accent: "amber",
    tagline: "A full teardown of any single company.",
    what: "Per-company analysis across 4 lenses — where you could launch it, what customers hate today, features it's missing, and pricing gaps — ending in a plain-English \"Should I build this?\" verdict.",
    problem: "It turns a company you admire into a concrete build plan: which market is open, what to fix, and how to win.",
    steps: [
      "Search or filter to a company, open its teardown.",
      "Read the green/amber \"Should I build this?\" banner at the top.",
      "Scroll the 4 lenses + the \"big idea\" synthesis, then Share or save it.",
    ],
    tips: [
      "Start with the verdict banner — ✅ \"Worth building\" means a strong gap AND a way to win.",
      "The \"What customers hate today\" lens shows real complaints any new entrant must beat — gold for positioning.",
      "Share a teardown link — it unfurls as a rich preview card in WhatsApp/Slack.",
    ],
  },
  {
    id: "signals", title: "Signals", tab: "opportunities", icon: Crosshair, accent: "rose",
    tagline: "Raw, unfiltered demand from where people are complaining.",
    what: "Live opportunities the scanner surfaces from real pain points (Reddit, freelancer marketplaces, etc.), each scored and stamped with a data-grounded Competition Gate verdict.",
    problem: "This is the bottom-up engine: real problems people are actively struggling with right now, before they're obvious.",
    steps: [
      "Browse the scored opportunity cards.",
      "Check the Competition Gate badge (GREEN/YELLOW/RED) — it overrides hype with real competitor data.",
      "Click a card for the full breakdown and the best country to launch.",
    ],
    tips: [
      "Use the \"trending up\" filter to see what's rising in Google Trends right now.",
      "RED verdicts aren't failures — they tell you the lane is crowded so you can pick a sharper wedge.",
      "Look for the \"Best: {Country}\" badge to jump straight to the localization angle.",
    ],
  },
  {
    id: "ai_pulse", title: "AI Pulse", tab: "ai_pulse", icon: Lightning, accent: "violet",
    tagline: "The freshest, highest-signal opportunities.",
    what: "A curated stream of the strongest recent opportunities with their Competition Gate verdicts and a self-audit banner showing how many got downgraded by real competition data.",
    problem: "A fast daily read on what's hot and genuinely open — with honesty about how much of it survives a competition check.",
    steps: [
      "Scan the top cards for high P2P scores.",
      "Watch for struck-through scores — that means the Competition Gate downgraded the hype.",
      "Open a card to see the named competitors and their GitHub stars / funding.",
    ],
    tips: [
      "Trust the downgrade %: a high number means the engine is honestly filtering noise for you.",
      "GREEN + high demand = the rare combination worth acting on fast.",
    ],
  },
  {
    id: "trending", title: "Trending", tab: "trending", icon: TrendUp, accent: "blue",
    tagline: "12,500+ topics by real Google search volume.",
    what: "An Exploding-Topics-scale board of demand topics across 11 verticals — each ranked by real search volume + 12-month growth, then stamped with a Verdict (Emerging / Competitive / Saturated / 💎 Whitespace) and the funded startups already building it.",
    problem: "See exactly what people are searching for and whether anyone is serving that demand yet.",
    steps: [
      "Use the 📈 Topics view, filter by section/category or search a market.",
      "Tap 💎 Emerging for proven-demand markets with few players and room — that's where the real opportunities are. 💎 Whitespace / 🟢 Underserved are SERP-verified and rare (often empty).",
      "Open a topic to see \"who's building this\" + ask the Advisor.",
    ],
    tips: [
      "Sort by \"Best opportunity\" to blend demand and low competition automatically.",
      "💎 Emerging = proven demand + few funded players + room to enter — the most actionable state and where most real opportunities live.",
      "Switch to 🚀 Startup Models / Startups to see momentum from the VC corpus.",
    ],
  },
  {
    id: "meta", title: "Meta Trends", tab: "trending", icon: Compass, accent: "violet",
    tagline: "The big-picture movements behind the noise.",
    what: "We group the 7,000+ individual demand topics into ~42 larger thematic movements (e.g. \"Generative AI Boom\", \"Work-From-Home Fitness\"), each grounded in real volume + the verdict mix (💎 Emerging / Competitive / 🔴 Saturated) shown inside.",
    problem: "Founders think in movements, not single keywords. Meta Trends show the forest, then let you drill into the trees.",
    steps: [
      "In Trending, switch to the 🧭 Meta Trends view.",
      "Sort by Biggest demand, 💎 Most whitespace, Fastest growing or Most topics.",
      "Open a movement to see its stats and every topic inside, filterable by verdict.",
    ],
    tips: [
      "Sort by 💎 Most whitespace to find movements full of unbuilt opportunities.",
      "Use the verdict filter inside a movement to jump straight to the open lanes.",
    ],
  },
  {
    id: "country", title: "Best Country", tab: "country_fit", icon: Globe, accent: "emerald",
    tagline: "Where each opportunity fits best.",
    what: "For every analyzed signal we recommend the country it's most likely to win in, with an Agree/Disagree confidence badge and a full side-by-side comparison.",
    problem: "The same idea can be saturated in the US but wide open elsewhere — this tells you where to launch.",
    steps: [
      "Open Best Country and browse the recommended-country list.",
      "Click any row for the full side-by-side market comparison.",
      "Use it together with a Discover/Evaluate opportunity to lock your launch geography.",
    ],
    tips: [
      "Pair this with a 💎 Whitespace topic to find demand that's open in a specific country.",
      "The coverage bar shows how much data backs each recommendation — prefer higher coverage.",
    ],
  },
  {
    id: "briefs", title: "My Briefs", tab: "briefs", icon: NotePencil, accent: "zinc",
    tagline: "Your saved shortlist.",
    what: "A personal library of the opportunities and briefs you've saved, so you can compare a focused shortlist instead of re-searching every time.",
    problem: "Keeps your best finds in one place as you move from exploring to deciding.",
    steps: [
      "Save any opportunity or brief from its detail view.",
      "Open My Briefs to review your shortlist.",
      "Compare, revisit and decide what to build.",
    ],
    tips: [
      "Build a shortlist of 3–5 before deciding — the contrast makes the winner obvious.",
      "Re-open saved briefs and ask the Advisor to compare them head-to-head.",
    ],
  },
];

const FAQ = [
  { q: "Is this real data or AI guesses?", a: "Real data. Every number — search volume, verdicts, revenue, competitor counts — is computed from real sources (Google search volume, YC/VC corpus, TrustMRR revenue, GitHub/Hacker News). The AI only writes plain-English summaries and labels; it never invents figures. When a source is missing, we say \"verify before acting\" instead of faking it." },
  { q: "How fresh is the data?", a: "The corpus auto-refreshes on a recurring schedule (the date is shown on Discover/Evaluate headers). Trending search volumes come from real Google Ads volume pulled in batches. We label freshness honestly everywhere." },
  { q: "What does 💎 Whitespace mean?", a: "A topic with strong, proven demand (high search volume), expressed as a real multi-word market phrase, that zero funded startups are clearly building yet. It's the most valuable verdict — an open lane with demand already there." },
  { q: "What's the difference between the two engines?", a: "Proven Winners (Discover + Evaluate) is top-down: start from models already working and find where they're missing. Demand Signals (Signals + Trending + Meta Trends) is bottom-up: start from raw demand and pain people show right now. Same trust rules either way." },
  { q: "What is the Competition Gate?", a: "Before recommending you build something, we run real product-landscape searches (GitHub, Hacker News, web) and count established competitors. A crowded space gets downgraded to RED/YELLOW even if demand is high — so you don't walk into a saturated market." },
  { q: "What do the Verdicts mean?", a: "Emerging = proven but few players, room to enter. Competitive = real market, need a sharp wedge. Saturated = many funded rivals, crowded. 💎 Whitespace = demand with nobody building. Unvalidated = a single keyword, validate demand first." },
  { q: "How do I get started fast?", a: "1) Ask the Advisor a question about your space. 2) Open Discover and sort by gap score. 3) Check Trending → 🧭 Meta Trends and sort by 💎 Most whitespace. 4) Run your favorite through Evaluate and Best Country. 5) Save winners to My Briefs." },
  { q: "Is the AI ever wrong?", a: "It can be — that's why everything is grounded and cited. Always click through to the sources before acting on a claim. Where we couldn't verify something, we flag it openly rather than hide it." },
];

const GLOSSARY = [
  { term: "Verdict", def: "A topic's demand-vs-competition status: Emerging, Competitive, Saturated, 💎 Whitespace or Unvalidated." },
  { term: "💎 Whitespace", def: "A market where a live Google check finds ZERO products ranking — a truly open lane. Verified, not assumed, so it's rare (often none)." },
  { term: "🟢 Underserved", def: "A market where only 1–3 real products rank (no big retailers) — a thin lane with room to enter. SERP-verified, so also rare." },
  { term: "Meta Trend", def: "A group of many related topics rolled up into one larger movement (e.g. \"Work-From-Home Fitness\")." },
  { term: "Gap Score", def: "How strong an opportunity is in Discover. ≥3 is strong, ≥5 is rare." },
  { term: "Competition Gate", def: "A real-data competitor check that can downgrade an over-optimistic recommendation." },
  { term: "Indie-proven", def: "A bootstrapped product with real self-reported monthly revenue solving a similar problem." },
  { term: "P2P score", def: "The weighted opportunity score on Signals/AI Pulse cards (demand + pain + monetization)." },
];

const ACCENT = {
  violet: "bg-violet-50 text-violet-700 ring-violet-200",
  amber: "bg-amber-50 text-amber-700 ring-amber-200",
  rose: "bg-rose-50 text-rose-700 ring-rose-200",
  blue: "bg-blue-50 text-blue-700 ring-blue-200",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  zinc: "bg-zinc-100 text-zinc-600 ring-zinc-200",
};

const STEPS_2ENGINE = [
  { n: 1, t: "Pick your starting point", d: "Top-down from a proven model (Discover/Evaluate) or bottom-up from raw demand (Signals/Trending)." },
  { n: 2, t: "Find the open lane", d: "Use Verdicts, 💎 Whitespace and the Competition Gate to spot demand nobody's serving yet." },
  { n: 3, t: "Localize & validate", d: "Best Country tells you where to launch; the Advisor pressure-tests your pick — all grounded in real data." },
  { n: 4, t: "Shortlist & build", d: "Save winners to My Briefs, compare, and move from idea to a concrete build plan." },
];

const FeatureCard = ({ f, onNavigate, highlight }) => {
  const Icon = f.icon;
  return (
    <div id={`guide-feature-${f.id}`} className={`bg-white border rounded-2xl p-5 sm:p-6 transition-all duration-500 scroll-mt-24 ${highlight ? "border-violet-400 ring-2 ring-violet-200 shadow-[0_8px_40px_-12px_rgba(139,92,246,0.5)]" : "border-zinc-200"}`} data-testid={`guide-feature-${f.id}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className={`flex-shrink-0 w-10 h-10 rounded-xl ring-1 flex items-center justify-center ${ACCENT[f.accent]}`}>
          <Icon size={20} weight="duotone" />
        </div>
        <div className="min-w-0">
          <h3 className="font-extrabold text-zinc-900 text-lg leading-tight">{f.title}</h3>
          <p className="text-sm text-zinc-500">{f.tagline}</p>
        </div>
      </div>

      <p className="text-sm text-zinc-700 leading-relaxed mb-2"><b className="text-zinc-900">What it is:</b> {f.what}</p>
      <p className="text-sm text-zinc-600 leading-relaxed mb-4"><b className="text-zinc-900">Why it matters:</b> {f.problem}</p>

      <div className="mb-4">
        <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5">How to use it</div>
        <ol className="space-y-1.5">
          {f.steps.map((s, i) => (
            <li key={i} className="flex gap-2 text-sm text-zinc-600">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-zinc-100 text-zinc-500 text-[11px] font-bold flex items-center justify-center">{i + 1}</span>
              {s}
            </li>
          ))}
        </ol>
      </div>

      <div className="rounded-xl bg-violet-50/60 border border-violet-100 p-3.5 mb-4">
        <div className="text-[11px] font-bold uppercase tracking-wider text-violet-600 mb-1.5 inline-flex items-center gap-1"><Lightbulb size={13} weight="fill" /> Get the best results</div>
        <ul className="space-y-1.5">
          {f.tips.map((t, i) => (
            <li key={i} className="flex gap-2 text-sm text-zinc-700"><CheckCircle size={16} weight="fill" className="flex-shrink-0 text-violet-500 mt-0.5" />{t}</li>
          ))}
        </ul>
      </div>

      <button onClick={() => onNavigate && onNavigate(f.tab)} data-testid={`guide-goto-${f.id}`}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-zinc-900 text-white text-sm font-semibold hover:bg-violet-600 transition-colors">
        Take me to {f.title} <ArrowRight size={15} weight="bold" />
      </button>
    </div>
  );
};

const FEATURE_TITLE = { advisor: "Advisor", discover: "Discover", evaluate: "Evaluate", signals: "Signals", ai_pulse: "AI Pulse", trending: "Trending", meta: "Meta Trends", country: "Best Country", briefs: "My Briefs", guide: "Guide (tour/help button)" };

const AdminInsights = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/analytics/help-clicks`).then((r) => setData(r.data)).catch(() => setData({ total: 0, items: [] }));
  }, []);
  if (!data) return null;
  const max = Math.max(1, ...(data.items || []).map((i) => i.clicks));
  return (
    <section className="mt-12 rounded-2xl border border-zinc-200 bg-zinc-50/60 p-5 sm:p-6" data-testid="guide-admin-insights">
      <div className="flex items-center gap-2 mb-1">
        <ChartBar size={18} weight="duotone" className="text-violet-600" />
        <h2 className="text-base font-bold text-zinc-900">Help analytics <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 ml-1">admin only</span></h2>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Which “?” help buttons get clicked most — your map of where users get confused. {data.total} total clicks · {data.total_7d} in the last 7 days.</p>
      {(!data.items || data.items.length === 0) ? (
        <div className="text-sm text-zinc-400 py-4">No help clicks recorded yet. As users tap the “?” icons, the most-confusing screens will surface here.</div>
      ) : (
        <div className="space-y-2">
          {data.items.map((it) => (
            <div key={it.feature} className="flex items-center gap-3" data-testid={`guide-insight-${it.feature}`}>
              <div className="w-32 sm:w-40 flex-shrink-0 text-sm text-zinc-700 truncate">{FEATURE_TITLE[it.feature] || it.feature}</div>
              <div className="flex-1 h-5 rounded-full bg-zinc-100 overflow-hidden">
                <div className="h-full bg-violet-500 rounded-full transition-all" style={{ width: `${(it.clicks / max) * 100}%` }} />
              </div>
              <div className="w-20 flex-shrink-0 text-right text-xs tabular-nums text-zinc-600"><b className="text-zinc-900">{it.clicks}</b> <span className="text-zinc-400">({it.clicks_7d}/7d)</span></div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const FaqItem = ({ item, open, onToggle, idx }) => (
  <div className="border border-zinc-200 rounded-xl overflow-hidden bg-white" data-testid={`guide-faq-${idx}`}>
    <button onClick={onToggle} className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left" data-testid={`guide-faq-toggle-${idx}`}>
      <span className="font-semibold text-zinc-900 text-sm">{item.q}</span>
      <CaretDown size={16} className={`flex-shrink-0 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && <div className="px-4 pb-4 text-sm text-zinc-600 leading-relaxed">{item.a}</div>}
  </div>
);

export default function GuideDemo({ onNavigate, target, onConsumeTarget }) {
  const [q, setQ] = useState("");
  const [openFaq, setOpenFaq] = useState(0);
  const [highlight, setHighlight] = useState(null);

  // When opened with a target feature (via a contextual "?" deep-link), clear
  // any active search, scroll that card into view and pulse-highlight it.
  React.useEffect(() => {
    if (!target) return;
    setQ("");
    const t1 = setTimeout(() => {
      const el = document.getElementById(`guide-feature-${target}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlight(target);
      }
      if (onConsumeTarget) onConsumeTarget();
    }, 120);
    const t2 = setTimeout(() => setHighlight(null), 2800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = q.trim().toLowerCase();
  const matches = (txt) => !query || (txt || "").toLowerCase().includes(query);

  const features = useMemo(() => FEATURES.filter((f) =>
    matches(f.title) || matches(f.tagline) || matches(f.what) || matches(f.problem) ||
    f.steps.some(matches) || f.tips.some(matches)
  ), [query]);
  const faqs = useMemo(() => FAQ.filter((f) => matches(f.q) || matches(f.a)), [query]);
  const glossary = useMemo(() => GLOSSARY.filter((g) => matches(g.term) || matches(g.def)), [query]);
  const empty = features.length === 0 && faqs.length === 0 && glossary.length === 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="guide-demo-view">
      {/* Hero */}
      <div className="text-xs font-bold tracking-widest text-violet-600 mb-2 inline-flex items-center gap-1.5"><BookOpen size={13} weight="fill" /> GUIDE &amp; DEMO</div>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-zinc-900 mb-2">Everything you need to get the best results.</h1>
      <p className="text-zinc-500 max-w-3xl text-sm">
        New here, or have a question? This is your one-stop guide. Search any feature or doubt below, learn exactly how each tool works, and get pro tips to find your next startup faster. Every answer links you straight to the right place.
      </p>

      {/* Search */}
      <div className="relative mt-5 mb-8 max-w-2xl">
        <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="guide-search"
          placeholder="Ask a question or search a feature… (e.g. whitespace, pricing, how to start)"
          className="w-full pl-10 pr-4 py-3 rounded-2xl border border-zinc-200 bg-white text-sm focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100" />
      </div>

      {empty && (
        <div className="text-center py-16 text-zinc-500" data-testid="guide-no-results">
          <MagnifyingGlass size={28} className="mx-auto text-zinc-300 mb-2" />
          No matches for “{q}”. Try a feature name like <b>Discover</b>, <b>Trending</b> or <b>whitespace</b>.
        </div>
      )}

      {/* How it works (hidden while searching) */}
      {!query && (
        <section className="mb-10" data-testid="guide-how-it-works">
          <h2 className="text-lg font-bold text-zinc-900 mb-1">How Prime Pillar works</h2>
          <p className="text-sm text-zinc-500 mb-4">Two engines, one goal: find a startup worth building — backed by real evidence, not guesses.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {STEPS_2ENGINE.map((s) => (
              <div key={s.n} className="bg-white border border-zinc-200 rounded-2xl p-4">
                <div className="w-8 h-8 rounded-full bg-violet-600 text-white font-bold flex items-center justify-center mb-2">{s.n}</div>
                <div className="font-semibold text-zinc-900 text-sm mb-1">{s.t}</div>
                <div className="text-xs text-zinc-500 leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4 flex items-start gap-3" data-testid="guide-trust-moat">
            <ShieldCheck size={22} weight="duotone" className="flex-shrink-0 text-emerald-600 mt-0.5" />
            <div>
              <div className="font-semibold text-zinc-900 text-sm">The trust rule that runs through everything</div>
              <p className="text-sm text-zinc-600 leading-relaxed">Every hard claim is backed by a real source or openly flagged “verify before acting.” The AI writes the words; the data carries the facts. That's what makes our verdicts trustworthy.</p>
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      {features.length > 0 && (
        <section className="mb-10" data-testid="guide-features">
          <h2 className="text-lg font-bold text-zinc-900 mb-4">Every feature, explained</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {features.map((f) => <FeatureCard key={f.id} f={f} onNavigate={onNavigate} highlight={highlight === f.id} />)}
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="mb-10" data-testid="guide-faq-section">
          <h2 className="text-lg font-bold text-zinc-900 mb-4">Frequently asked questions</h2>
          <div className="space-y-2 max-w-3xl">
            {faqs.map((item) => {
              const realIdx = FAQ.indexOf(item);
              return <FaqItem key={realIdx} idx={realIdx} item={item} open={openFaq === realIdx} onToggle={() => setOpenFaq(openFaq === realIdx ? -1 : realIdx)} />;
            })}
          </div>
        </section>
      )}

      {/* Glossary */}
      {glossary.length > 0 && (
        <section data-testid="guide-glossary">
          <h2 className="text-lg font-bold text-zinc-900 mb-4">Glossary — our key terms</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {glossary.map((g) => (
              <div key={g.term} className="bg-white border border-zinc-200 rounded-xl p-4">
                <div className="font-bold text-zinc-900 text-sm mb-0.5">{g.term}</div>
                <div className="text-sm text-zinc-600 leading-relaxed">{g.def}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Admin-only: which help buttons get clicked most */}
      {!query && typeof window !== "undefined" && localStorage.getItem("pp_admin_token") && <AdminInsights />}

      {/* Bottom CTA */}
      {!query && (
        <div className="mt-12 rounded-2xl bg-zinc-900 text-white p-6 sm:p-8 text-center" data-testid="guide-bottom-cta">
          <h3 className="text-xl font-extrabold mb-2">Still have a doubt?</h3>
          <p className="text-zinc-300 text-sm mb-4 max-w-xl mx-auto">Ask the Advisor anything — it answers using your real data and cites its evidence.</p>
          <button onClick={() => onNavigate && onNavigate("copilot")} data-testid="guide-cta-advisor"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-violet-600 text-white font-semibold hover:bg-violet-500 transition-colors">
            <ChatCircleDots size={17} weight="fill" /> Ask the Advisor
          </button>
        </div>
      )}
    </div>
  );
}
